import {
  ExternalLink,
  File,
  FileArchive,
  FileAudio,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
} from "lucide-react";

/**
 * A trailing extension is the only signal an artefact entry carries about what
 * it is, so it picks the icon. It says nothing about what the URL beside it
 * serves, which is why the action does not read as a download.
 */
const FILE_ICONS: Record<string, typeof File> = {
  "7z": FileArchive,
  aac: FileAudio,
  avi: FileVideo,
  csv: FileSpreadsheet,
  doc: FileText,
  docx: FileText,
  gif: FileImage,
  gz: FileArchive,
  jpeg: FileImage,
  jpg: FileImage,
  json: FileCode,
  m4a: FileAudio,
  md: FileText,
  mov: FileVideo,
  mp3: FileAudio,
  mp4: FileVideo,
  pdf: FileText,
  png: FileImage,
  rar: FileArchive,
  svg: FileImage,
  tar: FileArchive,
  ts: FileCode,
  tsx: FileCode,
  txt: FileText,
  wav: FileAudio,
  webm: FileVideo,
  webp: FileImage,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  zip: FileArchive,
};

/** The extension, when the name carries one that could plausibly be a file. */
export function extensionOf(path: string): string | null {
  return /\.([a-z0-9]{2,5})$/i.exec(path)?.[1]?.toLowerCase() ?? null;
}

/** The icon for a material, by extension. A link gets the link glyph. */
export function materialIcon(path: string): typeof File {
  const extension = extensionOf(path);
  if (!extension) {
    return ExternalLink;
  }
  return FILE_ICONS[extension] ?? File;
}

/** "3 files", "1 link" or "4 items" when an exam mixes the two. */
export function materialsMeta(paths: readonly string[]): string {
  const links = paths.filter((path) => extensionOf(path) === null);
  const noun =
    links.length === paths.length
      ? "link"
      : links.length === 0
        ? "file"
        : "item";
  return `${String(paths.length)} ${noun}${paths.length === 1 ? "" : "s"}`;
}
